import vc from '@digitalbazaar/vc';
import contexts from '@digitalbazaar/vc/lib/contexts';
import { extendContextLoader } from 'jsonld-signatures';
import ed25519Context from 'ed25519-signature-2020-context';
import * as jose from 'jose';
import { Ed25519VerificationKey2020 } from
  '@digitalbazaar/ed25519-verification-key-2020';
import { Ed25519Signature2020 } from '@digitalbazaar/ed25519-signature-2020';

// append 2020 signature suite to cached contexts
contexts[ed25519Context.CONTEXT_URL] = ed25519Context.CONTEXT;
// setup static document loader
const documentLoader = extendContextLoader(async function documentLoader(url) {
  const context = contexts[url];
  if (context !== undefined) {
    return {
      contextUrl: null,
      documentUrl: url,
      document: context
    };
  }
  throw new Error(`Document loader unable to load URL "${url}".`);
});

function addContext(url, context) {
  contexts[url] = context;
}

async function loadContexts(exampleContexts) {
  const promises = new Array();
  for (const url of exampleContexts) {
    let context = contexts[url];
    if (context === undefined) {
      promises.push(new Promise(async (resolve, reject) => {
        const response = await fetch(url);
        if (response.ok) {
          context = await response.json();
          addContext(url, context);
          resolve();
        } else {
          reject(`Cannot fetch ${url}`);
        }
      }));
    }
  }
  await Promise.all(promises);
}

// convert an XML Schema v1.` Datetime value to a UNIX timestamp
function xmlDateTimeToUnixTimestamp(xmlDateTime) {
  if (!xmlDateTime) {
    return undefined;
  }

  return Date.parse(xmlDateTime) / 1000;
}

// transform the input credential to a signed JWT
async function transformCredentialToJwt({ credential, kid, jwk }) {
  const header = { alg: 'RS256', typ: 'JWT' };
  const payload = {
    vc: credential
  };
  if (credential.expirationDate) {
    payload.exp = xmlDateTimeToUnixTimestamp(credential.expirationDate);
  }
  if (credential.issuer) {
    payload.iss = credential.issuer;
  }
  if (credential.issuanceDate) {
    payload.nbf = xmlDateTimeToUnixTimestamp(credential.issuanceDate);
  }
  if (credential.id) {
    payload.jti = credential.id;
  }
  if (credential.credentialSubject.id) {
    payload.sub = credential.credentialSubject.id;
  }

  const jwt = await new jose.SignJWT(payload)
    .setProtectedHeader(header)
    .sign(jwk.privateKey);

  return { header, payload, jwt };
};

// transform the input credential to a JWT description
async function transformCredentialToJwtDescription({ credential, kid, jwk }) {
  const { header, payload, jwt } = await transformCredentialToJwt({ credential, kid, jwk });

  // create the JWT description
  let description = '---------------- JWT header ---------------\n' +
    JSON.stringify(header, null, 2);

  description += '\n\n--------------- JWT payload ---------------\n' +
    '// NOTE: The example below uses a valid VC-JWT serialization\n' +
    '//       that duplicates the iss, nbf, jti, and sub fields in the\n' +
    '//       Verifiable Credential (vc) field.\n\n' +
    JSON.stringify(payload, null, 2);

  return description + '\n\n--------------- JWT ---------------\n\n' + jwt;
};

// transform the input presentation to a JWT
async function transformPresentationToJwt({ presentation, kid, jwk }) {
  const header = { alg: 'RS256', typ: 'JWT' };
  const payload = {
    vp: presentation
  };
  if (presentation.holder) {
    payload.iss = presentation.holder;
  }
  if (presentation.id) {
    payload.jti = presentation.id;
  }

  // Not required, but useful to verifier
  payload.iat = Math.round(new Date().getTime() / 1000);
  payload.nbf = payload.iat;
  payload.exp = payload.iat + (24 * 60 * 60);

  const jwt = await new jose.SignJWT(payload)
    .setProtectedHeader(header)
    .sign(jwk.privateKey);

  return { header, payload, jwt };
};

// transform the input presentation to a JWT description
async function transformPresentationToJwtDescription({ presentation, kid, jwk }) {
  const { header, payload, jwt } = await transformPresentationToJwt({ presentation, kid, jwk });

  // create the JWT description
  let description = '---------------- JWT header ---------------\n' +
    JSON.stringify(header, null, 2);

  description += '\n\n--------------- JWT payload ---------------\n' +
    '// NOTE: The example below uses a valid VP-JWT serialization\n' +
    '//       that duplicates the iss and jti fields in the\n' +
    '//       Verifiable Presentation (vp) field.\n\n' +
    JSON.stringify(payload, null, 2);

  return description + '\n\n--------------- JWT ---------------\n\n' + jwt;
};

async function attachCredentialProof({ credential, suite }) {
  const credentialCopy = JSON.parse(JSON.stringify(credential));
  return vc.issue({ credential: credentialCopy, suite, documentLoader });
};

async function attachPresentationProof({ presentation, suite }) {
  const presentationCopy = JSON.parse(JSON.stringify(presentation));
  return vc.signPresentation({ presentation: presentationCopy, challenge: "challenge", suite, documentLoader });
};

function addVcExampleStyles() {
  const exampleStyles = document.createElement('style');

  exampleStyles.innerHTML += `
  .vc-tabbed, .vp-tabbed {
    overflow-x: hidden;
    margin: 0 0;
  }

  .vc-tabbed [type="radio"], .vp-tabbed [type="radio"] {
    display: none;
  }

  .vc-tabs, .vp-tabs {
    display: flex;
    align-items: stretch;
    list-style: none;
    padding: 0;
    border-bottom: 1px solid #ccc;
  }

  li.vc-tab, li.vp-tab {
    margin: unset;
  }

  .vc-tab > label, .vp-tab > label {
    display: block;
    margin-bottom: -1px;
    padding: .4em .5em;
    border: 1px solid #ccc;
    border-top-right-radius: .4em;
    border-top-left-radius: .4em;
    background: #eee;
    color: #666;
    cursor: pointer;	
    transition: all 0.3s;
  }
  .vc-tab:hover label, .vp-tab:hover {
    border-left-color: #333;
    border-top-color: #333;
    border-right-color: #333;
    color: #333;
  }
  
  .vc-tab-content, .vp-tab-content {
    display: none;
  }

  .vc-tabbed [type="radio"]:nth-of-type(1):checked ~ .vc-tabs .vc-tab:nth-of-type(1) label,
  .vc-tabbed [type="radio"]:nth-of-type(2):checked ~ .vc-tabs .vc-tab:nth-of-type(2) label,
  .vc-tabbed [type="radio"]:nth-of-type(3):checked ~ .vc-tabs .vc-tab:nth-of-type(3) label {
    border-bottom-color: #fff;
    background: #fff;
    color: #222;
  }
  
  .vc-tabbed [type="radio"]:nth-of-type(1):checked ~ .vc-tab-content:nth-of-type(1),
  .vc-tabbed [type="radio"]:nth-of-type(2):checked ~ .vc-tab-content:nth-of-type(2),
  .vc-tabbed [type="radio"]:nth-of-type(3):checked ~ .vc-tab-content:nth-of-type(3) {
    display: block;
  }

  .vp-tabbed [type="radio"]:nth-of-type(1):checked ~ .vp-tabs .vp-tab:nth-of-type(1) label,
  .vp-tabbed [type="radio"]:nth-of-type(2):checked ~ .vp-tabs .vp-tab:nth-of-type(2) label,
  .vp-tabbed [type="radio"]:nth-of-type(3):checked ~ .vp-tabs .vp-tab:nth-of-type(3) label {
    border-bottom-color: #fff;
    background: #fff;
    color: #222;
  }
  
  .vp-tabbed [type="radio"]:nth-of-type(1):checked ~ .vp-tab-content:nth-of-type(1),
  .vp-tabbed [type="radio"]:nth-of-type(2):checked ~ .vp-tab-content:nth-of-type(2),
  .vp-tabbed [type="radio"]:nth-of-type(3):checked ~ .vp-tab-content:nth-of-type(3) {
    display: block;
  }`;

  document.getElementsByTagName('head')[0].appendChild(exampleStyles);
}

function createCredentialTabs(example, proofExampleIndex, verifiableCredentialProof, verifiableCredentialJwt) {
  // set up the tabbed content
  const tabbedContent = document.createElement('div');
  tabbedContent.setAttribute('class', 'vc-tabbed');

  // set up the unsigned button
  const unsignedTabBtn = document.createElement('input');
  unsignedTabBtn.setAttribute('type', 'radio');
  unsignedTabBtn.setAttribute('id', `vc-tab${proofExampleIndex}1`);
  unsignedTabBtn.setAttribute('name', `vc-tabs${proofExampleIndex}`);
  unsignedTabBtn.setAttribute('checked', 'checked');
  tabbedContent.appendChild(unsignedTabBtn);

  // set up the signed proof button
  const signedProofTabBtn = document.createElement('input');
  signedProofTabBtn.setAttribute('type', 'radio');
  signedProofTabBtn.setAttribute('id', `vc-tab${proofExampleIndex}2`);
  signedProofTabBtn.setAttribute('name', `vc-tabs${proofExampleIndex}`);
  tabbedContent.appendChild(signedProofTabBtn);

  // set up the signed JWT button
  const signedJwtTabBtn = document.createElement('input');
  signedJwtTabBtn.setAttribute('type', 'radio');
  signedJwtTabBtn.setAttribute('id', `vc-tab${proofExampleIndex}3`);
  signedJwtTabBtn.setAttribute('name', `vc-tabs${proofExampleIndex}`);
  tabbedContent.appendChild(signedJwtTabBtn);

  // set up the tab labels
  const tabLabels = document.createElement("ul");
  tabLabels.setAttribute('class', 'vc-tabs');
  tabbedContent.appendChild(tabLabels);

  const unsignedLabel = document.createElement("li");
  unsignedLabel.setAttribute('class', 'vc-tab');
  unsignedLabel.innerHTML = `<label for='${unsignedTabBtn.getAttribute('id')}'>Credential</label>`;
  tabLabels.appendChild(unsignedLabel)

  const signedProofLabel = document.createElement("li");
  signedProofLabel.setAttribute('class', 'vc-tab');
  signedProofLabel.innerHTML = `<label for='${signedProofTabBtn.getAttribute('id')}'>Verifiable Credential (with proof)</label>`;
  tabLabels.appendChild(signedProofLabel)

  const signedJwtLabel = document.createElement("li");
  signedJwtLabel.setAttribute('class', 'vc-tab');
  signedJwtLabel.innerHTML = `<label for='${signedJwtTabBtn.getAttribute('id')}'>Verifiable Credential (as JWT)</label>`;
  tabLabels.appendChild(signedJwtLabel)

  // append the tabbed content
  const container = example.parentNode;
  const unsignedContent = document.createElement('div');
  unsignedContent.setAttribute('class', 'vc-tab-content');
  // Move the credential example to the unsigned tab
  unsignedContent.append(example);
  tabbedContent.appendChild(unsignedContent);

  const signedProofContent = document.createElement('div');
  signedProofContent.setAttribute('class', 'vc-tab-content');
  signedProofContent.innerHTML = `<pre>${JSON.stringify(verifiableCredentialProof, null, 2).match(/.{1,80}/g).join('\n')}</pre>`;
  tabbedContent.appendChild(signedProofContent);

  const signedJwtContent = document.createElement('div');
  signedJwtContent.setAttribute('class', 'vc-tab-content');
  signedJwtContent.innerHTML = `<pre>${verifiableCredentialJwt.match(/.{1,80}/g).join('\n')}</pre>`;
  tabbedContent.appendChild(signedJwtContent);

  // replace the original example with the tabbed content

  container.append(tabbedContent);
}

function createPresentationTabs(example, proofExampleIndex, verifiablePresentationProof, verifiablePresentationJwt) {

  // set up the tabbed content
  const tabbedContent = document.createElement('div');
  tabbedContent.setAttribute('class', 'vp-tabbed');

  // set up the unsigned button
  const unsignedTabBtn = document.createElement('input');
  unsignedTabBtn.setAttribute('type', 'radio');
  unsignedTabBtn.setAttribute('id', `vp-tab${proofExampleIndex}1`);
  unsignedTabBtn.setAttribute('name', `vp-tabs${proofExampleIndex}`);
  unsignedTabBtn.setAttribute('checked', 'checked');
  tabbedContent.appendChild(unsignedTabBtn);

  // set up the signed proof button
  const signedProofTabBtn = document.createElement('input');
  signedProofTabBtn.setAttribute('type', 'radio');
  signedProofTabBtn.setAttribute('id', `vp-tab${proofExampleIndex}2`);
  signedProofTabBtn.setAttribute('name', `vp-tabs${proofExampleIndex}`);
  tabbedContent.appendChild(signedProofTabBtn);

  // set up the signed JWT button
  const signedJwtTabBtn = document.createElement('input');
  signedJwtTabBtn.setAttribute('type', 'radio');
  signedJwtTabBtn.setAttribute('id', `vp-tab${proofExampleIndex}3`);
  signedJwtTabBtn.setAttribute('name', `vp-tabs${proofExampleIndex}`);
  tabbedContent.appendChild(signedJwtTabBtn);

  // set up the tab labels
  const tabLabels = document.createElement("ul");
  tabLabels.setAttribute('class', 'vp-tabs');
  tabbedContent.appendChild(tabLabels);

  const unsignedLabel = document.createElement("li");
  unsignedLabel.setAttribute('class', 'vp-tab');
  unsignedLabel.innerHTML = `<label for='${unsignedTabBtn.getAttribute('id')}'>Presentation</label>`;
  tabLabels.appendChild(unsignedLabel)

  const signedProofLabel = document.createElement("li");
  signedProofLabel.setAttribute('class', 'vp-tab');
  signedProofLabel.innerHTML = `<label for='${signedProofTabBtn.getAttribute('id')}'>Verifiable Presentation (with proof)</label>`;
  tabLabels.appendChild(signedProofLabel)

  const signedJwtLabel = document.createElement("li");
  signedJwtLabel.setAttribute('class', 'vp-tab');
  signedJwtLabel.innerHTML = `<label for='${signedJwtTabBtn.getAttribute('id')}'>Verifiable Presentation (as JWT)</label>`;
  tabLabels.appendChild(signedJwtLabel)

  // append the tabbed content

  const container = example.parentNode;
  const unsignedContent = document.createElement('div');
  unsignedContent.setAttribute('class', 'vp-tab-content');
  // Move the presentation example to the unsigned tab
  unsignedContent.append(example);
  tabbedContent.appendChild(unsignedContent);

  const signedProofContent = document.createElement('div');
  signedProofContent.setAttribute('class', 'vp-tab-content');
  signedProofContent.innerHTML = `<pre>${JSON.stringify(verifiablePresentationProof, null, 2).match(/.{1,80}/g).join('\n')}</pre>`;
  tabbedContent.appendChild(signedProofContent);

  const signedJwtContent = document.createElement('div');
  signedJwtContent.setAttribute('class', 'vp-tab-content');
  signedJwtContent.innerHTML = `<pre>${verifiablePresentationJwt.match(/.{1,80}/g).join('\n')}</pre>`;
  tabbedContent.appendChild(signedJwtContent);

  // replace the original example with the tabbed content

  container.append(tabbedContent);
}

async function createCredentialExamples(suite, jwk) {

  // process every example that needs a vc-proof
  const vcProofExamples = document.querySelectorAll(".vc");
  let proofExampleIndex = 0;
  for (const example of vcProofExamples) {
    proofExampleIndex++;
    const verificationMethod = example.getAttribute('data-vc-vm');
    suite.verificationMethod =
      verificationMethod || 'did:key:' + suite.key.publicKey;

    // extract and sign the example
    let credential = {};
    try {
      let exampleText = example.innerText;
      exampleText = exampleText.replace(/\/\/ .*$/gm, '');
      credential = JSON.parse(exampleText);
    } catch (e) {
      console.error('respec-vc error: Failed to create Verifiable Credential.',
        e, example.innerText);
      continue;
    }

    const assertionProofs = new Array();
    const assertionJwts = new Array();

    if (Array.from(credential.type).includes("ClrCredential")) {

      // process each nested credential
      for (const assertion of credential.credentialSubject.assertions) {

        // pre-load the contexts
        await loadContexts(assertion["@context"]);

        // attach the proof
        let verifiableCredentialProof;
        try {
          verifiableCredentialProof = await attachCredentialProof({ credential: assertion, suite });
        } catch (e) {
          console.error(
            'respec-vc error: Failed to attach proof to Assertion.',
            e, JSON.stringify(credential, 2));
          continue;
        }

        // convert to JWT
        let verifiableCredentialJwt;
        try {
          const { header, payload, jwt } = await transformCredentialToJwt({ credential: assertion, kid: suite.verificationMethod, jwk });
          verifiableCredentialJwt = jwt;
        } catch (e) {
          console.error(
            'respec-vc error: Failed to convert Assertion to JWT.',
            e, JSON.stringify(credential, 2));
          continue;
        }

        assertionProofs.push(verifiableCredentialProof);
        assertionJwts.push(verifiableCredentialJwt);
      }
    }

    // pre-load the contexts
    await loadContexts(credential["@context"]);

    // attach the proof
    let verifiableCredentialProof;
    try {
      if (Array.from(credential.type).includes("ClrCredential")) {
        credential.credentialSubject.assertions = assertionProofs;
      }
      verifiableCredentialProof = await attachCredentialProof({ credential, suite });
    } catch (e) {
      console.error(
        'respec-vc error: Failed to attach proof to Verifiable Credential.',
        e, example.innerText);
      continue;
    }

    // convert to a JWT
    let verifiableCredentialJwt;
    try {
      if (Array.from(credential.type).includes("ClrCredential")) {
        credential.credentialSubject.assertions = assertionJwts;
      }
      verifiableCredentialJwt = await transformCredentialToJwtDescription({
        credential, kid: suite.verificationMethod, jwk
      });
    } catch (e) {
      console.error(
        'respec-vc error: Failed to convert Credential to JWT.',
        e, example.innerText);
      continue;
    }

    createCredentialTabs(example, proofExampleIndex, verifiableCredentialProof, verifiableCredentialJwt);
  }
}

async function createPresentationExamples(suite, jwk) {
  // process every example that needs a vp-proof
  const vpProofExamples = document.querySelectorAll(".vp");
  let proofExampleIndex = 0;
  for (const example of vpProofExamples) {
    proofExampleIndex++;
    const verificationMethod = example.getAttribute("data-vp-vm");
    suite.verificationMethod =
      verificationMethod || "did:key:" + suite.key.publicKey;

    // extract and sign the example
    const originalText = example.innerHTML;
    let presentation = {};
    try {
      let exampleText = example.innerText;
      exampleText = exampleText.replace(/\/\/ .*$/gm, '');
      presentation = JSON.parse(exampleText);
    } catch (e) {
      console.error(
        'respec-vc error: Failed to create Presentation.',
        e, example.innerText);
      continue;
    }

    const credentialJwts = new Array();
    const credentialProofs = new Array();

    // process each nested credential
    for (const credential of presentation.verifiableCredential) {

      const assertionJwts = new Array();
      const assertionProofs = new Array();

      if (Array.from(credential.type).includes("ClrCredential")) {

        // process each nested credential
        for (const assertion of credential.credentialSubject.assertions) {

          // pre-load the contexts
          await loadContexts(assertion["@context"]);

          // attach the proof
          let verifiableCredentialProof;
          try {
            verifiableCredentialProof = await attachCredentialProof({ credential: assertion, suite });
          } catch (e) {
            console.error(
              'respec-vc error: Failed to attach proof to Assertion.',
              e, JSON.stringify(credential, 2));
            continue;
          }

          // convert to JWT
          let verifiableCredentialJwt;
          try {
            const { header, payload, jwt } = await transformCredentialToJwt({ credential: assertion, kid: suite.verificationMethod, jwk });
            verifiableCredentialJwt = jwt;
          } catch (e) {
            console.error(
              'respec-vc error: Failed to convert Assertion to JWT.',
              e, JSON.stringify(credential, 2));
            continue;
          }

          assertionProofs.push(verifiableCredentialProof);
          assertionJwts.push(verifiableCredentialJwt);
        }
      }

      // pre-load the contexts
      await loadContexts(credential["@context"]);

      // attach the proof
      let verifiableCredentialProof;
      try {
        if (Array.from(credential.type).includes("ClrCredential")) {
          credential.credentialSubject.assertions = assertionProofs;
        }
        verifiableCredentialProof = await attachCredentialProof({ credential, suite });
      } catch (e) {
        console.error(
          'respec-vc error: Failed to attach proof to Credential.',
          e, JSON.stringify(credential, 2));
        continue;
      }

      // convert to JWT
      let verifiableCredentialJwt;
      try {
        if (Array.from(credential.type).includes("ClrCredential")) {
          credential.credentialSubject.assertions = assertionJwts;
        }
        const { header, payload, jwt } = await transformCredentialToJwt({ credential, kid: suite.verificationMethod, jwk });
        verifiableCredentialJwt = jwt;
      } catch (e) {
        console.error(
          'respec-vc error: Failed to convert Credential to JWT.',
          e, JSON.stringify(credential, 2));
        continue;
      }

      credentialProofs.push(verifiableCredentialProof);
      credentialJwts.push(verifiableCredentialJwt);
    }

    // pre-load the contexts
    await loadContexts(presentation["@context"]);

    // attach the proof
    let verifiablePresentationProof;
    try {
      presentation.verifiableCredential = credentialProofs;
      verifiablePresentationProof = await attachPresentationProof({ presentation, suite });
    } catch (e) {
      console.error(
        'respec-vc error: Failed to attach proof to Presentation.',
        e, example.innerText);
      continue;
    }

    // convert to a JWT
    let verifiablePresentationJwt;
    try {
      presentation.verifiableCredential = credentialJwts;
      verifiablePresentationJwt = await transformPresentationToJwtDescription({
        presentation, kid: suite.verificationMethod, jwk
      });
    } catch (e) {
      console.error(
        'respec-vc error: Failed to convert Presentation to JWT.',
        e, example.innerText);
      continue;
    }

    createPresentationTabs(example, proofExampleIndex, verifiablePresentationProof, verifiablePresentationJwt);
  }
}

async function createVcExamples() {
  // add styles for examples
  addVcExampleStyles();

  // generate base keypair and signature suite
  const keyPair = await Ed25519VerificationKey2020.generate();
  const suite = new Ed25519Signature2020({
    key: keyPair
  });
  const jwk = await jose.generateKeyPair('RS256');

  await createCredentialExamples(suite, jwk);
  await createPresentationExamples(suite, jwk);
}

// setup exports on window
window.respecVc = {
  addContext,
  createVcExamples
}
